// Student rank lifecycle hooks (#747).
//
// Reads come from student_ranks + student_rank_state_log directly (RLS allows
// any company member to read). Writes go through the SECURITY DEFINER RPCs
// added in supabase/migrations/20260516000001_belt_lifecycle_rpcs.sql —
// authenticated callers have no direct INSERT/UPDATE/DELETE on student_ranks.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../useCurrentCompany';

export interface StudentRankRow {
  id: string;
  student_id: string;
  company_id: string;
  sport_id: string;
  current_rank_id: string;
  state: 'active' | 'on_hold';
  achieved_at: string;
  accumulated_time_on_hold_seconds: number;
  hold_started_at: string | null;
  notes: string | null;
  // Joined client-side for display.
  rank_name?: string;
  rank_order?: number;
  profile_name?: string;
  sport_name?: string;
}

export interface StateLogRow {
  id: string;
  student_rank_id: string;
  from_state: string | null;
  to_state: string;
  reason: string | null;
  changed_by: string;
  changed_at: string;
}

export const studentRanksKeys = {
  all: ['student-ranks'] as const,
  forStudent: (studentId: string, companyId: string | null | undefined) =>
    [...studentRanksKeys.all, studentId, companyId ?? 'none'] as const,
  stateLog: (studentRankId: string) =>
    [...studentRanksKeys.all, 'log', studentRankId] as const,
};

/**
 * All rank rows for one student in the current company. Joins rank + profile
 * + sport names client-side so the UI can render a single table.
 */
export function useStudentRanks(studentId: string | undefined) {
  const { data: companyId } = useCurrentCompany();

  return useQuery({
    queryKey: studentRanksKeys.forStudent(studentId ?? 'none', companyId),
    queryFn: async (): Promise<StudentRankRow[]> => {
      if (!studentId || !companyId) return [];

      const { data: ranks, error } = await supabase
        .from('student_ranks')
        .select('*')
        .eq('student_id', studentId)
        .eq('company_id', companyId)
        .order('state', { ascending: true }); // 'active' < 'on_hold' alphabetically
      if (error) throw error;
      const rows = (ranks ?? []) as StudentRankRow[];
      if (rows.length === 0) return rows;

      // Hydrate display fields. Three small lookups; cheaper than one nested
      // select due to the deep-instantiation issue we hit in #746.
      const rankIds = Array.from(new Set(rows.map((r) => r.current_rank_id)));
      const sportIds = Array.from(new Set(rows.map((r) => r.sport_id)));

      const [ranksRes, sportsRes] = await Promise.all([
        supabase
          .from('belt_ranks')
          .select('id, name, rank_order, profile_id')
          .in('id', rankIds),
        supabase
          .from('company_sports')
          .select('id, sport_name')
          .in('id', sportIds),
      ]);
      if (ranksRes.error) throw ranksRes.error;
      if (sportsRes.error) throw sportsRes.error;

      const profileIds = Array.from(
        new Set((ranksRes.data ?? []).map((r) => r.profile_id))
      );
      const profilesRes = await supabase
        .from('belt_profiles')
        .select('id, name')
        .in('id', profileIds);
      if (profilesRes.error) throw profilesRes.error;

      const rankById = new Map(
        (ranksRes.data ?? []).map((r) => [r.id, r])
      );
      const profileById = new Map(
        (profilesRes.data ?? []).map((p) => [p.id, p])
      );
      const sportById = new Map(
        (sportsRes.data ?? []).map((s) => [s.id, s])
      );

      return rows.map((r) => {
        const rankInfo = rankById.get(r.current_rank_id);
        const profileInfo = rankInfo ? profileById.get(rankInfo.profile_id) : undefined;
        return {
          ...r,
          rank_name: rankInfo?.name,
          rank_order: rankInfo?.rank_order,
          profile_name: profileInfo?.name,
          sport_name: sportById.get(r.sport_id)?.sport_name,
        };
      });
    },
    enabled: !!studentId && !!companyId,
    staleTime: 30_000,
  });
}

/**
 * Audit trail for a single student_rank row. Drives the per-row history
 * drawer in the editor UI.
 */
export function useStudentRankStateLog(studentRankId: string | undefined) {
  return useQuery({
    queryKey: studentRanksKeys.stateLog(studentRankId ?? 'none'),
    queryFn: async (): Promise<StateLogRow[]> => {
      if (!studentRankId) return [];
      const { data, error } = await supabase
        .from('student_rank_state_log')
        .select('*')
        .eq('student_rank_id', studentRankId)
        .order('changed_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as StateLogRow[];
    },
    enabled: !!studentRankId,
    staleTime: 30_000,
  });
}

// ============================================================================
// Mutations — wrap the two SECURITY DEFINER RPCs.
// ============================================================================

export interface AssignInitialRankInput {
  studentId: string;
  sportId: string;
  rankId?: string | null; // null = pick lowest rank_order in any profile bound to (company, sport)
  isTransferSetup?: boolean;
  setActive?: boolean;
  notes?: string | null;
}

export function useAssignInitialRank() {
  const queryClient = useQueryClient();
  const { data: companyId } = useCurrentCompany();

  return useMutation({
    mutationFn: async (input: AssignInitialRankInput) => {
      if (!companyId) throw new Error('No active company');
      const { data, error } = await supabase.rpc(
        'assign_student_initial_rank',
        {
          p_student_id: input.studentId,
          p_company_id: companyId,
          p_sport_id: input.sportId,
          p_rank_id: input.rankId ?? null,
          p_is_transfer_setup: input.isTransferSetup ?? false,
          p_set_active: input.setActive ?? true,
          p_notes: input.notes ?? null,
        }
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_id, input) => {
      queryClient.invalidateQueries({
        queryKey: studentRanksKeys.forStudent(input.studentId, companyId),
      });
    },
  });
}

export interface SwitchActiveDisciplineInput {
  studentId: string;
  targetSportId: string;
}

export function useSwitchActiveDiscipline() {
  const queryClient = useQueryClient();
  const { data: companyId } = useCurrentCompany();

  return useMutation({
    mutationFn: async (input: SwitchActiveDisciplineInput) => {
      if (!companyId) throw new Error('No active company');
      const { data, error } = await supabase.rpc('switch_active_discipline', {
        p_student_id: input.studentId,
        p_company_id: companyId,
        p_target_sport_id: input.targetSportId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_id, input) => {
      queryClient.invalidateQueries({
        queryKey: studentRanksKeys.forStudent(input.studentId, companyId),
      });
    },
  });
}
